<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useConfirm, useToast } from "primevue";
import { formatDate } from "@persolog/utils";
import { LanguageManagementAPI } from "../api/index.ts";
import { BannerAudience, BannerImage, BannerImageVariant, BannerState, StartPageBanner } from "../types/BannerTypes.ts";
import {
    BANNER_LANGUAGES,
    bannerState,
    displayAuthor,
    emptyStartPageBanner,
    endFromPicker,
    filledLanguages,
    formatImageDimensions,
    formatImageSize,
    formatWindowDate,
    imageUploadProblem,
    pickerDate,
    RECOMMENDED_IMAGE_DIMENSIONS,
    REQUIRED_LANGUAGE,
    reorderStartPageBanners,
    setStartPageBannerAlt,
    startFromPicker,
    startPageBannerAlt,
    startPageBannerProblem,
} from "../utils/bannerUtils.ts";

const { t } = useI18n();
const confirm = useConfirm();
const toast = useToast();

const banners = ref<StartPageBanner[]>([]);
const loading = ref(false);
const saving = ref(false);
const uploading = ref<BannerImageVariant | null>(null);
const dialogOpen = ref(false);
const edited = ref<StartPageBanner>(emptyStartPageBanner());
const editedLanguage = ref(REQUIRED_LANGUAGE);

// The interface languages of the CUSTOMER-FACING frontends, not this app's own: the alt text has to
// be writable in every language a visitor can pick (RDT-339).
const languages = BANNER_LANGUAGES;

const audiences: BannerAudience[] = ["ALL", "DE", "INT"];

const severityByState: Record<BannerState, string> = {
    active: "success",
    scheduled: "info",
    expired: "secondary",
    inactive: "warn",
};

const problem = computed(() => startPageBannerProblem(edited.value));

const orderedBanners = computed(() =>
    [...banners.value].sort((a, b) => a.position - b.position || (a.id ?? 0) - (b.id ?? 0)),
);

/**
 * The url the start pages will request. Same path here as there, proxied to language-management by
 * this app's nginx.conf, so the preview cannot show something a visitor would not get (RDT-359).
 */
function imageUrl(image: BannerImage | null | undefined): string {
    return image?.path ?? "";
}

async function load() {
    loading.value = true;
    try {
        banners.value = await LanguageManagementAPI.getStartPageBanners();
    } catch (error) {
        console.error("Failed to load start page banners:", error);
        toast.add({ severity: "error", summary: t("startPageBanners.toast.loadFailed"), life: 5000 });
    } finally {
        loading.value = false;
    }
}

function openCreate() {
    // At the end of the carousel: a new campaign does not silently jump the queue.
    edited.value = emptyStartPageBanner(banners.value.length);
    editedLanguage.value = REQUIRED_LANGUAGE;
    dialogOpen.value = true;
}

function openEdit(banner: StartPageBanner) {
    // Deep-copy the texts too, so cancelling really discards the edit.
    edited.value = { ...banner, texts: banner.texts.map((text) => ({ ...text })) };
    editedLanguage.value = REQUIRED_LANGUAGE;
    dialogOpen.value = true;
}

async function save() {
    if (problem.value) return;
    saving.value = true;
    try {
        if (edited.value.id === null) {
            await LanguageManagementAPI.createStartPageBanner(edited.value);
        } else {
            await LanguageManagementAPI.updateStartPageBanner(edited.value.id, edited.value);
        }
        dialogOpen.value = false;
        toast.add({ severity: "success", summary: t("startPageBanners.toast.saved"), life: 3000 });
        await load();
    } catch (error) {
        console.error("Failed to save start page banner:", error);
        toast.add({ severity: "error", summary: t("startPageBanners.toast.saveFailed"), life: 5000 });
    } finally {
        saving.value = false;
    }
}

function remove(banner: StartPageBanner) {
    confirm.require({
        message: t("startPageBanners.confirmDelete"),
        header: t("startPageBanners.delete"),
        icon: "pi pi-exclamation-triangle",
        rejectProps: { label: t("buttons.cancel"), severity: "secondary", outlined: true },
        acceptProps: { label: t("startPageBanners.delete"), severity: "danger" },
        accept: async () => {
            try {
                await LanguageManagementAPI.deleteStartPageBanner(banner.id as number);
                toast.add({ severity: "success", summary: t("startPageBanners.toast.deleted"), life: 3000 });
                await load();
            } catch (error) {
                console.error("Failed to delete start page banner:", error);
                toast.add({ severity: "error", summary: t("startPageBanners.toast.deleteFailed"), life: 5000 });
            }
        },
    });
}

/** Toggling active is the one action frequent enough to belong in the row rather than the dialog. */
async function toggleActive(banner: StartPageBanner) {
    try {
        await LanguageManagementAPI.updateStartPageBanner(banner.id as number, { ...banner, active: !banner.active });
        await load();
    } catch (error) {
        console.error("Failed to toggle start page banner:", error);
        toast.add({ severity: "error", summary: t("startPageBanners.toast.saveFailed"), life: 5000 });
        await load();
    }
}

/** Moves one banner through the carousel; only the rows whose position actually changed are saved. */
async function move(banner: StartPageBanner, direction: -1 | 1) {
    const changed = reorderStartPageBanners(banners.value, banner.id as number, direction);
    if (changed.length === 0) return;
    try {
        await Promise.all(
            changed.map((moved) => LanguageManagementAPI.updateStartPageBanner(moved.id as number, moved)),
        );
    } catch (error) {
        console.error("Failed to reorder start page banners:", error);
        toast.add({ severity: "error", summary: t("startPageBanners.toast.saveFailed"), life: 5000 });
    } finally {
        await load();
    }
}

// Held by template ref rather than looked up by id: getElementById only finds the input once the
// dialog is in the document, which is true in the app but not for a detached mount.
const fileInputs: Partial<Record<BannerImageVariant, HTMLInputElement>> = {};

function registerFileInput(variant: BannerImageVariant, element: unknown) {
    if (element instanceof HTMLInputElement) {
        fileInputs[variant] = element;
    } else {
        delete fileInputs[variant];
    }
}

/** Opens the hidden file picker for a slot, so the visible control can be a real button. */
function pickImage(variant: BannerImageVariant) {
    fileInputs[variant]?.click();
}

/**
 * Uploads one slot's image. What comes back is what the api STORED — scaled to the slot's width and
 * re-encoded as WebP — so the dialog shows the delivered size rather than the one that was picked.
 */
async function uploadImage(event: Event, variant: BannerImageVariant) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Reset the input either way, so picking the same file twice fires `change` again.
    input.value = "";
    if (!file) return;

    const rejected = imageUploadProblem(file);
    if (rejected) {
        toast.add({ severity: "error", summary: t(rejected), life: 5000 });
        return;
    }

    uploading.value = variant;
    try {
        const image = await LanguageManagementAPI.uploadBannerImage(file, variant);
        if (variant === "DESKTOP") {
            edited.value.desktopImageHash = image.hash;
            edited.value.desktopImage = image;
        } else {
            edited.value.mobileImageHash = image.hash;
            edited.value.mobileImage = image;
        }
        toast.add({
            severity: "success",
            summary: t("startPageBanners.toast.imageUploaded", {
                size: formatImageSize(image.byteSize),
                dimensions: formatImageDimensions(image),
            }),
            life: 4000,
        });
    } catch (error) {
        console.error("Failed to upload banner image:", error);
        toast.add({ severity: "error", summary: t("startPageBanners.toast.imageFailed"), life: 5000 });
    } finally {
        uploading.value = null;
    }
}

/** Detaches the optional mobile image; the desktop one is required and can only be replaced. */
function clearMobileImage() {
    edited.value.mobileImageHash = null;
    edited.value.mobileImage = null;
}

const startDate = computed({
    get: () => pickerDate(edited.value.startsAt),
    set: (date: Date | null) => (edited.value.startsAt = startFromPicker(date)),
});

const endDate = computed({
    get: () => pickerDate(edited.value.endsAt),
    set: (date: Date | null) => (edited.value.endsAt = endFromPicker(date)),
});

const editedAlt = computed({
    get: () => startPageBannerAlt(edited.value, editedLanguage.value),
    // Mutates instead of reassigning `edited`: see setStartPageBannerAlt — replacing the object on
    // every keystroke re-rendered the dialog and dropped characters out of fast typing (RDT-339).
    set: (text: string) => setStartPageBannerAlt(edited.value, editedLanguage.value, text),
});

const editedLink = computed({
    get: () => edited.value.link ?? "",
    set: (link: string) => (edited.value.link = link.trim() === "" ? null : link),
});

onMounted(load);
</script>

<template>
    <div class="p-4">
        <Toolbar class="mb-4">
            <template #start>
                <div>
                    <h1 class="text-2xl m-0">{{ t("startPageBanners.title") }}</h1>
                    <!-- Says out loud what the absence of a stage switch means, so nobody edits a
                         production banner in the wrong admin and wonders why nothing changed. -->
                    <small class="text-color-secondary">{{ t("startPageBanners.environmentHint") }}</small>
                </div>
            </template>
            <template #end>
                <Button
                    id="start_page_banners_btn_create"
                    data-cy="start-page-banner-create"
                    icon="pi pi-plus"
                    :label="t('startPageBanners.create')"
                    @click="openCreate"
                />
            </template>
        </Toolbar>

        <Message severity="info" :closable="false" class="mb-4" data-cy="start-page-banner-hint">
            {{ t("startPageBanners.fallbackHint") }}
        </Message>

        <DataTable
            :value="orderedBanners"
            :loading="loading"
            data-key="id"
            data-cy="start-page-banner-table"
            striped-rows
            :empty-message="t('startPageBanners.empty')"
        >
            <!-- Grouped by the question each column answers rather than one column per field, the
                 same way the notification banner list is (RDT-339 review). -->
            <Column :header="t('startPageBanners.column.preview')">
                <template #body="{ data }">
                    <img
                        v-if="data.desktopImage"
                        :src="imageUrl(data.desktopImage)"
                        :alt="startPageBannerAlt(data, 'en')"
                        class="start-page-banner-thumb"
                        :data-cy="`start-page-banner-thumb-${data.id}`"
                    />
                </template>
            </Column>
            <Column :header="t('banners.column.state')">
                <template #body="{ data }">
                    <div class="flex flex-col gap-1">
                        <Tag
                            :value="t(`banners.state.${bannerState(data)}`)"
                            :severity="severityByState[bannerState(data)]"
                            :data-cy="`start-page-banner-state-${data.id}`"
                            class="self-start"
                        />
                        <small
                            class="text-color-secondary whitespace-nowrap"
                            :data-cy="`start-page-banner-window-${data.id}`"
                        >
                            <template v-if="data.startsAt || data.endsAt">
                                {{ formatWindowDate(data.startsAt) }} – {{ formatWindowDate(data.endsAt) }}
                            </template>
                            <template v-else>{{ t("banners.window.always") }}</template>
                        </small>
                    </div>
                </template>
            </Column>
            <Column :header="t('startPageBanners.column.altAndLink')" class="w-1/4">
                <template #body="{ data }">
                    <div class="flex flex-col gap-1">
                        <span :data-cy="`start-page-banner-alt-${data.id}`">
                            {{ startPageBannerAlt(data, "en") }}
                        </span>
                        <small
                            v-if="data.link"
                            class="text-color-secondary start-page-banner-link"
                            :title="data.link"
                            :data-cy="`start-page-banner-link-${data.id}`"
                        >
                            {{ data.link }}
                        </small>
                        <small class="text-color-secondary">
                            {{ filledLanguages(data).map((language) => language.toUpperCase()).join(", ") }}
                        </small>
                    </div>
                </template>
            </Column>
            <Column :header="t('startPageBanners.column.delivered')">
                <template #body="{ data }">
                    <div
                        class="flex flex-col text-color-secondary text-sm"
                        :data-cy="`start-page-banner-images-${data.id}`"
                    >
                        <span>
                            {{ t("startPageBanners.variant.DESKTOP") }}:
                            {{ formatImageDimensions(data.desktopImage) }},
                            {{ formatImageSize(data.desktopImage?.byteSize) }}
                        </span>
                        <span v-if="data.mobileImage">
                            {{ t("startPageBanners.variant.MOBILE") }}:
                            {{ formatImageDimensions(data.mobileImage) }},
                            {{ formatImageSize(data.mobileImage?.byteSize) }}
                        </span>
                        <span v-else>{{ t("startPageBanners.noMobileImage") }}</span>
                        <span>{{ t(`banners.audience.${data.audience}`) }}</span>
                    </div>
                </template>
            </Column>
            <Column :header="t('banners.column.updated')">
                <template #body="{ data }">
                    <div
                        v-if="data.updatedAt"
                        class="flex flex-col text-color-secondary text-sm"
                        :title="data.updatedBy ?? ''"
                        :data-cy="`start-page-banner-updated-${data.id}`"
                    >
                        <span>{{ displayAuthor(data.updatedBy) }}</span>
                        <span>{{ formatDate(data.updatedAt) }}</span>
                    </div>
                </template>
            </Column>
            <Column :header="t('banners.column.actions')">
                <template #body="{ data, index }">
                    <div class="flex items-center gap-1">
                        <ToggleSwitch
                            :model-value="data.active"
                            :data-cy="`start-page-banner-toggle-${data.id}`"
                            :aria-label="t('banners.active')"
                            @update:model-value="toggleActive(data)"
                        />
                        <!-- The carousel cycles through several banners, so its order is part of the
                             configuration and not something to leave to insertion time. -->
                        <Button
                            icon="pi pi-arrow-up"
                            text
                            :disabled="index === 0"
                            :aria-label="t('startPageBanners.moveUp')"
                            :data-cy="`start-page-banner-up-${data.id}`"
                            @click="move(data, -1)"
                        />
                        <Button
                            icon="pi pi-arrow-down"
                            text
                            :disabled="index === orderedBanners.length - 1"
                            :aria-label="t('startPageBanners.moveDown')"
                            :data-cy="`start-page-banner-down-${data.id}`"
                            @click="move(data, 1)"
                        />
                        <Button
                            icon="pi pi-pencil"
                            text
                            :aria-label="t('banners.edit')"
                            :data-cy="`start-page-banner-edit-${data.id}`"
                            @click="openEdit(data)"
                        />
                        <Button
                            icon="pi pi-trash"
                            text
                            severity="danger"
                            :aria-label="t('startPageBanners.delete')"
                            :data-cy="`start-page-banner-delete-${data.id}`"
                            @click="remove(data)"
                        />
                    </div>
                </template>
            </Column>
        </DataTable>

        <!-- Each view that confirms renders its own: ConfirmDialog is not global the way Toast is
             in App.vue, so without this the delete button silently did nothing (RDT-339). -->
        <ConfirmDialog />

        <Dialog
            v-model:visible="dialogOpen"
            modal
            :header="edited.id === null ? t('startPageBanners.create') : t('startPageBanners.edit')"
            :style="{ width: '56rem' }"
            data-cy="start-page-banner-dialog"
        >
            <div class="flex flex-col gap-5">
                <!-- The images come first: they are the thing being published, and everything
                     below only qualifies them. -->
                <Fieldset :legend="t('startPageBanners.imagesLegend')">
                    <div class="flex flex-wrap gap-6">
                        <div v-for="variant in ['DESKTOP', 'MOBILE'] as BannerImageVariant[]" :key="variant">
                            <div class="flex flex-col gap-2">
                                <label :for="`start_page_banners_file_${variant}`" class="font-semibold">
                                    {{ t(`startPageBanners.variant.${variant}`) }}
                                    <span v-if="variant === 'DESKTOP'">*</span>
                                </label>
                                <!-- Above the preview on purpose: this is what to aim for when
                                     picking a file, while the line under the input reports what the
                                     api actually stored. -->
                                <small
                                    class="text-color-secondary"
                                    :data-cy="`start-page-banner-recommended-${variant}`"
                                >
                                    {{
                                        t("startPageBanners.recommended", {
                                            dimensions: formatImageDimensions(
                                                RECOMMENDED_IMAGE_DIMENSIONS[variant],
                                            ),
                                        })
                                    }}
                                </small>
                                <img
                                    v-if="variant === 'DESKTOP' ? edited.desktopImage : edited.mobileImage"
                                    :src="imageUrl(variant === 'DESKTOP' ? edited.desktopImage : edited.mobileImage)"
                                    :alt="editedAlt"
                                    class="start-page-banner-preview"
                                    :data-cy="`start-page-banner-preview-${variant}`"
                                />
                                <div v-else class="start-page-banner-placeholder">
                                    {{ t("startPageBanners.noImage") }}
                                </div>
                                <!-- The native control is kept for the picking itself but taken out
                                     of sight: it renders an unstyled button nobody read as a button,
                                     next to a "no file selected" caption that came BACK after every
                                     successful upload, because the input is reset so the same file
                                     can be picked twice. The state is the preview and the line below
                                     it instead (RDT-359 review). -->
                                <input
                                    :id="`start_page_banners_file_${variant}`"
                                    :ref="(element) => registerFileInput(variant, element)"
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    class="start-page-banner-file-input"
                                    :disabled="uploading !== null"
                                    :data-cy="`start-page-banner-file-${variant}`"
                                    @change="uploadImage($event, variant)"
                                />
                                <Button
                                    :label="
                                        (variant === 'DESKTOP' ? edited.desktopImage : edited.mobileImage)
                                            ? t('startPageBanners.replaceImage')
                                            : t('startPageBanners.chooseImage')
                                    "
                                    icon="pi pi-upload"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    class="self-start"
                                    :disabled="uploading !== null"
                                    :data-cy="`start-page-banner-choose-${variant}`"
                                    @click="pickImage(variant)"
                                />
                                <small
                                    v-if="uploading === variant"
                                    class="text-color-secondary"
                                    :data-cy="`start-page-banner-uploading-${variant}`"
                                >
                                    {{ t("startPageBanners.uploading") }}
                                </small>
                                <small
                                    v-else-if="variant === 'DESKTOP' ? edited.desktopImage : edited.mobileImage"
                                    class="text-color-secondary"
                                    :data-cy="`start-page-banner-delivered-${variant}`"
                                >
                                    {{
                                        formatImageDimensions(
                                            variant === "DESKTOP" ? edited.desktopImage : edited.mobileImage,
                                        )
                                    }},
                                    {{
                                        formatImageSize(
                                            (variant === "DESKTOP" ? edited.desktopImage : edited.mobileImage)
                                                ?.byteSize,
                                        )
                                    }}
                                </small>
                                <Button
                                    v-if="variant === 'MOBILE' && edited.mobileImage"
                                    :label="t('startPageBanners.clearMobileImage')"
                                    severity="secondary"
                                    text
                                    size="small"
                                    class="self-start"
                                    data-cy="start-page-banner-clear-mobile"
                                    @click="clearMobileImage"
                                />
                            </div>
                        </div>
                    </div>
                    <!-- Says what the api does to an upload, so nobody goes looking for an image
                         editor first: it scales and re-encodes, and reports what it stored. -->
                    <small class="text-color-secondary block mt-3">{{ t("startPageBanners.uploadHint") }}</small>
                </Fieldset>

                <div class="flex flex-col gap-2">
                    <div class="flex items-center gap-3 flex-wrap">
                        <label for="start_page_banners_select_language" class="font-semibold">
                            {{ t("startPageBanners.alt") }}
                        </label>
                        <Select
                            id="start_page_banners_select_language"
                            v-model="editedLanguage"
                            :options="languages"
                            data-cy="start-page-banner-language"
                        >
                            <template #value="{ value }">
                                {{ value?.toUpperCase() }}<span v-if="value === REQUIRED_LANGUAGE"> *</span>
                            </template>
                            <template #option="{ option }">
                                {{ option.toUpperCase() }}
                                <i
                                    v-if="filledLanguages(edited).includes(option)"
                                    class="pi pi-check ml-2 text-xs"
                                />
                            </template>
                        </Select>
                    </div>
                    <!-- autofocus so the dialog's focus lands here rather than on the close button,
                         where a space would close the dialog mid-typing (RDT-339). -->
                    <InputText
                        id="start_page_banners_input_alt"
                        v-model="editedAlt"
                        autofocus
                        data-cy="start-page-banner-alt-input"
                    />
                    <!-- The hardcoded banner had one English alt for every viewer; per-language alt
                         text is the accessibility gap this view closes (RDT-359). -->
                    <small class="text-color-secondary">{{ t("startPageBanners.altHint") }}</small>
                </div>

                <div class="flex flex-col gap-2">
                    <label for="start_page_banners_input_link" class="font-semibold">
                        {{ t("startPageBanners.link") }}
                    </label>
                    <InputText
                        id="start_page_banners_input_link"
                        v-model="editedLink"
                        placeholder="https://academy.persolog.de/…"
                        data-cy="start-page-banner-link-input"
                    />
                    <small class="text-color-secondary">{{ t("startPageBanners.linkHint") }}</small>
                </div>

                <!-- Grouped by the question each answers — who sees it, and when — rather than one
                     flat list of controls (RDT-339 review). -->
                <Fieldset :legend="t('startPageBanners.whoLegend')">
                    <div class="flex flex-col gap-2">
                        <SelectButton
                            id="start_page_banners_select_audience"
                            v-model="edited.audience"
                            :options="audiences"
                            :option-label="(audience: BannerAudience) => t(`banners.audience.${audience}`)"
                            :allow-empty="false"
                            class="self-start"
                            data-cy="start-page-banner-audience"
                        />
                        <small class="text-color-secondary">{{ t("banners.audienceHint") }}</small>
                    </div>
                </Fieldset>

                <Fieldset :legend="t('banners.whenLegend')">
                    <div class="flex flex-col gap-3">
                        <div class="flex items-center gap-3">
                            <ToggleSwitch
                                id="start_page_banners_switch_active"
                                v-model="edited.active"
                                data-cy="start-page-banner-active"
                            />
                            <label for="start_page_banners_switch_active">{{ t("banners.active") }}</label>
                            <small class="text-color-secondary">{{ t("banners.activeHint") }}</small>
                        </div>

                        <div class="flex flex-wrap gap-4">
                            <div class="flex flex-col gap-2">
                                <label for="start_page_banners_date_start" class="text-color-secondary text-sm">
                                    {{ t("banners.startsAt") }}
                                </label>
                                <DatePicker
                                    id="start_page_banners_date_start"
                                    v-model="startDate"
                                    date-format="dd.mm.yy"
                                    show-button-bar
                                    data-cy="start-page-banner-start"
                                />
                            </div>
                            <div class="flex flex-col gap-2">
                                <label for="start_page_banners_date_end" class="text-color-secondary text-sm">
                                    {{ t("banners.endsAt") }}
                                </label>
                                <DatePicker
                                    id="start_page_banners_date_end"
                                    v-model="endDate"
                                    date-format="dd.mm.yy"
                                    show-button-bar
                                    data-cy="start-page-banner-end"
                                />
                            </div>
                        </div>
                        <small class="text-color-secondary">{{ t("banners.windowHint") }}</small>
                    </div>
                </Fieldset>

                <Message v-if="problem" severity="error" :closable="false" data-cy="start-page-banner-problem">
                    {{ t(problem) }}
                </Message>
            </div>

            <template #footer>
                <Button :label="t('buttons.cancel')" severity="secondary" text @click="dialogOpen = false" />
                <Button
                    :label="t('buttons.save')"
                    :disabled="problem !== null || saving || uploading !== null"
                    :loading="saving"
                    data-cy="start-page-banner-save"
                    @click="save"
                />
            </template>
        </Dialog>
    </div>
</template>

<style scoped>
/* Out of sight but still in the DOM and operable: the button above clicks it, the <label for> still
   points at it, and the Cypress specs still attach a file to it. */
.start-page-banner-file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

/* No border-radius anywhere: the persolog CI guide calls for square edges. */
.start-page-banner-thumb {
    width: 10rem;
    height: auto;
    max-height: 4rem;
    object-fit: contain;
    display: block;
}

.start-page-banner-preview {
    width: 20rem;
    max-height: 8rem;
    object-fit: contain;
    display: block;
    background-color: var(--p-surface-100);
}

.start-page-banner-placeholder {
    width: 20rem;
    height: 8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--p-surface-100);
    color: var(--p-text-muted-color);
    border: 1px dashed var(--p-surface-400);
}

.start-page-banner-link {
    display: block;
    max-width: 18rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
